import { inject, PLATFORM_ID } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, combineLatestWith, filter, map, switchMap, take } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';

import { RESPONSE } from '../../../express.token';
import { UserService } from '../../common/services/user.service';
import { SitemapService } from '../../common/services/sitemap.service';
import { GlobalService } from '../../common/services/global.service';
import { MetaService } from '../services/meta.service';

/**
 * Static page resolver
 * @param route 
 * @param state 
 * @returns 
 */
export const staticPageResolver: ResolveFn<any> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
): Observable<any> => {
    // Dependency injections
    const platformId = inject(PLATFORM_ID);
    const response = inject(RESPONSE, { optional: true });
    const userService = inject(UserService);
    const sitemapService = inject(SitemapService);
    const metaService = inject(MetaService);
    const globalService = inject(GlobalService);

    const path = state.url;
    const cleanUrl = state.url.startsWith('/') ? state.url.slice(1) : state.url;
    // `state.url` carries the query string, so strip it before matching path segments.
    const segments = path.split('?')[0].split('/').filter(Boolean);
    let type: string | null = null;

    if (segments[0] === 'agents' && segments.length === 2) {
        type = 'agents';
    } else if (segments[0] === 'sitemap' && segments.length === 2) {
        type = 'community';
    } else if (segments[0] === 'sitemap' && segments.length === 1) {
        type = 'sitemap';
    } else if (segments.length === 1 && segments[0]?.search('sitemap-listings') !== -1) {
        type = 'sitemap-listings';
    } else if (segments.length === 1 && segments[0]?.search('sitemap-blogs') !== -1) {
        type = 'sitemap-blogs';
    } else if (segments.length === 1 && segments[0]?.search('sitemap-pages') !== -1) {
        type = 'sitemap-pages';
    } else if ((segments[0] === 'schools' || segments[1] === 'schools') && segments.length <= 2) {
        type = 'schools';
    } else if ((segments[0] === 'zipcodes' || segments[1] === 'zipcodes') && segments.length <= 2) {
        type = 'zipcode';
    } else if (segments.length === 1 && segments[0] === 'mortgage-calculator') {
        type = 'mortgage-calculator';
    }

    switch (type) {
        case 'agents': {
            const slug = state.url?.split('/').filter(Boolean).pop()!;            
            return userService.getUserBySlug(slug).pipe(
                combineLatestWith(
                    globalService.companyDetails.pipe(
                        filter((details) => details !== null),
                        take(1),
                    ),
                ),
                map(([res]) => {
                    const agent = res?.body?.data?.agent;
                    const companyName = globalService.companyDetails.value?.name;
                    // Only set meta if agent and companyName exist
                    if (agent && companyName) {
                        const title = `${agent.full_name} | ${companyName}`;
                        const meta_description = `${companyName} - Real Estate Agents who are here to help when you're ready to buy or sell a home - ${agent.full_name} | ${companyName}`;
        
                        metaService.updateStaticPageMetaTags(
                            title,
                            meta_description,
                            cleanUrl,
                            agent.fub_picture,
                        );
                    }
                            
                    return agent;
                }),
                catchError((err) => {
                    if (err.status === 404) {
                        if (isPlatformServer(platformId)) {
                            response?.status(404);
                        }
                        globalService.isNotFoundPage.next(false);
                    }
                    return of(null);
                }),
            );
        }
        
        case 'community': {
            const metaService = inject(MetaService);

            return globalService.companyDetails
                .pipe(
                    filter((details) => details !== null),
                    take(1),
                    switchMap((details) => {
                        const lastPath = state.url?.split('/').filter(Boolean).pop()!;
                        const segments = lastPath.split('-');
                        const stateCodes = globalService.stateCodes.value;
                        let stateCode = globalService.companyDetails.value?.operating_state_settings?.default_state?.toLowerCase() || '';
                        let city = '';
                        if (segments.length > 1 && stateCodes.includes(segments[segments.length - 1].toLowerCase())) {
                            stateCode = segments[segments.length - 1].toLowerCase();
                            city = segments.slice(0, segments.length - 1).join('-');
                            city = city.split('-')
                                .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ');

                        } else {
                            city = state.url?.split('/').filter(Boolean).pop()!;   
                            city = city.split('-')
                                .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ');
                        }
                        return sitemapService.getCommunitiesByCity(stateCode, city).pipe(
                            combineLatestWith(
                                globalService.companyDetails.pipe(
                                    filter((details) => details !== null),
                                    take(1),
                                ),
                            ),
                            map(([res]) => {
                                const communities = res.body?.data?.communities;
                                const companyName = globalService.companyDetails.value?.name;
            
                                if (!communities || communities.length === 0) {
                                    if (isPlatformServer(platformId)) {
                                        response?.status(404);
                                    }
                                    globalService.isNotFoundPage.next(false);
                                    return null;
                                }
                    
                                if (companyName) {
                                    const cityName =
                                        city
                                            .split('-')
                                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                            .join(' ');
                    
                                    const title = `${cityName} Communities Sitemap | Homes for Sale & Real Estate | ${companyName}`;
                                    const meta_description = `${cityName} Communities Sitemap | Homes for Sale & Real Estate | ${companyName}`;
                                    metaService.updateStaticPageMetaTags(title, meta_description, cleanUrl);
                                }
                    
                                return communities;
                            }),
                            catchError((err) =>{
                                return of(null);
                            }),
                        );
                    })
                )

        }
        case 'zipcode':
        case 'schools': {
            return globalService.companyDetails.pipe(
                filter(details => !!details && !!details.name),
                take(1),
                switchMap(details => {
                    let isPageAllowed = true;
                    let metaText = 'Zip Codes'
                    if (type === 'schools') {
                        // isPageAllowed = globalService.listingPageSettings?.value?.pages.schools === 'allow';
                        metaText = 'Schools'
                    }

                    if (type === 'zipcode') {
                        // isPageAllowed = globalService.listingPageSettings?.value?.pages.zipcode === 'allow';
                        metaText = 'Zip Codes'
                    }
                    const companyName = globalService.companyDetails.value?.name;
                    if (!isPageAllowed) {
                        if (isPlatformServer(platformId)) {
                            response?.status(404);
                        }
                        globalService.isNotFoundPage.next(false);
                    } else {
                        if (companyName) {
                            let city = '';
                            const segments = state.url?.split('/')?.filter(Boolean) || [];
                            if (segments.length === 2) {
                                city = segments[0]
                                    .split('-')
                                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                            }
                            
                            let title = `${metaText} - Homes for Sale & Real Estate`;
                            let description = `${companyName} ${metaText} of Real Estate`;

                            if (city) {
                                title = `${metaText} - ${city} Homes for Sale & Real Estate`;
                                description = `${companyName} ${metaText} of ${city} Real Estate - ${metaText}`;
                            }
                            metaService.updateStaticPageMetaTags(title, description, cleanUrl);
                        }
                    }
                    return of(isPageAllowed);
                })
            );
        
        }
        case 'mortgage-calculator': {
            return globalService.companyDetails.pipe(
                filter(details => !!details && !!details.name),
                take(1),
                map(() => {
                    const companyName = globalService.companyDetails.value?.name;
                    const title = `Mortgage Calculator | ${companyName}`;
                    const meta_description = `Estimate your monthly mortgage payment with the mortgage calculator from ${companyName}.`;
                    metaService.updateStaticPageMetaTags(title, meta_description, cleanUrl);
                    return true;
                })
            );
        }
        case 'sitemap': 
        case 'sitemap-listings':
        case 'sitemap-blogs': 
        case 'sitemap-pages': {
            return globalService.companyDetails.pipe(
                filter(details => !!details && !!details.name),
                take(1),
                switchMap(details => {
                    const companyName = globalService.companyDetails.value?.name;
        
                    if (companyName) {
                        let title = '';
                        let meta_description = '';
                        let path = '';
                        if (type == 'sitemap') {
                            title = `City Sitemap | Homes for Sale & Real Estate | ${companyName}`;
                            meta_description = `City Sitemap | Homes for Sale & Real Estate | ${companyName}`;
                        }

                        if (type === 'sitemap-listings') {
                            title =  `Listings Sitemap | Homes for Sale &amp; Real Estate | ${globalService.companyDetails.value?.name}`;
                            meta_description = `Listings Sitemap | Homes for Sale &amp; Real Estate | ${globalService.companyDetails.value?.name}`;
                        }

                        if (type === 'sitemap-blogs') {
                            title = `Blogs Sitemap | Homes for Sale &amp; Real Estate | ${globalService.companyDetails.value?.name}`;
                            meta_description= `Blogs Sitemap | Homes for Sale &amp; Real Estate | ${globalService.companyDetails.value?.name}`;
                        }

                        if (type === 'sitemap-pages') {
                            title = `Pages Sitemap | Homes for Sale &amp; Real Estate | ${globalService.companyDetails.value?.name}`;
                            meta_description= `Pages Sitemap | Homes for Sale &amp; Real Estate | ${globalService.companyDetails.value?.name}`;
                        }

                        metaService.updateStaticPageMetaTags(
                            title,
                            meta_description,
                            cleanUrl
                        );
                    }
              
                  return of(true);
                })
            );
        }

        default:
            return of(true); // fallback for safety
    }
};
